export interface DiffLine {
  type: 'same' | 'add' | 'del';
  text: string;
}

export function computeDiff(before: string, after: string): DiffLine[] {
  const left = before.split('\n');
  const right = after.split('\n');

  let start = 0;
  while (
    start < left.length &&
    start < right.length &&
    left[start] === right[start]
  ) {
    start += 1;
  }
  let endLeft = left.length;
  let endRight = right.length;
  while (
    endLeft > start &&
    endRight > start &&
    left[endLeft - 1] === right[endRight - 1]
  ) {
    endLeft -= 1;
    endRight -= 1;
  }

  const midLeft = left.slice(start, endLeft);
  const midRight = right.slice(start, endRight);
  const out: DiffLine[] = left
    .slice(0, start)
    .map(text => ({ type: 'same' as const, text }));

  const n = midLeft.length;
  const m = midRight.length;
  if (n * m > 4_000_000) {
    out.push(...midLeft.map(text => ({ type: 'del' as const, text })));
    out.push(...midRight.map(text => ({ type: 'add' as const, text })));
  } else if (n || m) {
    const width = m + 1;
    const lcs = new Uint32Array((n + 1) * width);
    for (let i = n - 1; i >= 0; i -= 1) {
      for (let j = m - 1; j >= 0; j -= 1) {
        lcs[i * width + j] =
          midLeft[i] === midRight[j]
            ? lcs[(i + 1) * width + j + 1] + 1
            : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (midLeft[i] === midRight[j]) {
        out.push({ type: 'same', text: midLeft[i] });
        i += 1;
        j += 1;
      } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
        out.push({ type: 'del', text: midLeft[i] });
        i += 1;
      } else {
        out.push({ type: 'add', text: midRight[j] });
        j += 1;
      }
    }
    while (i < n) out.push({ type: 'del', text: midLeft[i++] });
    while (j < m) out.push({ type: 'add', text: midRight[j++] });
  }

  out.push(
    ...left.slice(endLeft).map(text => ({ type: 'same' as const, text })),
  );
  return out;
}

export type DiffChunk =
  | { kind: 'lines'; lines: DiffLine[] }
  | { kind: 'collapsed'; count: number };

export function compactDiff(lines: DiffLine[], context = 3): DiffChunk[] {
  const visible = new Array<boolean>(lines.length).fill(false);
  lines.forEach((line, index) => {
    if (line.type === 'same') return;
    for (
      let i = Math.max(0, index - context);
      i <= Math.min(lines.length - 1, index + context);
      i += 1
    ) {
      visible[i] = true;
    }
  });

  const chunks: DiffChunk[] = [];
  let index = 0;
  while (index < lines.length) {
    if (visible[index]) {
      const from = index;
      while (index < lines.length && visible[index]) index += 1;
      chunks.push({ kind: 'lines', lines: lines.slice(from, index) });
    } else {
      const from = index;
      while (index < lines.length && !visible[index]) index += 1;
      chunks.push({ kind: 'collapsed', count: index - from });
    }
  }
  return chunks;
}
